import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';

import { Logger } from 'aws-amplify';

import {
  Typography,
} from '@material-ui/core';

import { makeStyles } from '@material-ui/core/styles';

import { fmtS3ConsoleLink } from '../../utils/formatters';

const logger = new Logger('ExecutionInputLog');

const useStyles = makeStyles((theme) => ({
  root: {
    padding: '0px 3px',
    fontSize: 'small',
  },
  line: {
    display: 'flex',
  },
  index: {
    textAlign: 'right',
  },
  timestamp: {
    textAlign: 'right',
    minWidth: '75px',
  },
  controller: {
    fontWeight: 'bold',
    marginBottom: '1px',
    borderLeftWidth: '10px',
    borderLeftStyle: 'solid',
    borderLeftColor: `${theme.palette.primary.main}`,
    backgroundColor: `${theme.palette.primary.light}30`,
  },
  controllerInput: {
    marginTop: '3px',
  },
  controllerStart: {
  },
  controllerSample: {
  },
  workload: {
    borderLeftWidth: '10px',
    borderLeftStyle: 'solid',
    borderLeftColor: 'transparent',
    marginBottom: '1px',
  },
  workloadError: {
    borderLeftColor: theme.palette.error.light,
  },
  workloadWarning: {
    borderLeftColor: theme.palette.warning.light,
  },
  workloadDebug: {
    borderLeftColor: theme.palette.grey.A100,
  },
  controllerFinish: {
    marginBottom: '3px',
  },
  controllerFinishSuccess: {
    borderLeftColor: `${theme.palette.success.main}`,
    backgroundColor: `${theme.palette.success.light}30`,
  },
  controllerFinishFailed: {
    borderLeftColor: `${theme.palette.error.main}`,
    backgroundColor: `${theme.palette.error.light}30`,
  },
  controllerFinishSkipped: {
    borderLeftColor: `${theme.palette.warning.main}`,
    backgroundColor: `${theme.palette.warning.light}30`,
  },
}));

function fmtTimestamp(timestamp, format = 'datetime') {
  const dttm=new Date(timestamp);
  if(format==='datetime') {
    return dttm.toLocaleDateString()+" "+dttm.toLocaleTimeString();
  } else if(format==='time') {
    return dttm.toLocaleTimeString();
  }
}

const s3Regex=/s3:\/\/([^/]+)\/([^']*?([^/]+)\/?)$/gi;
const errorRegex=/\b(?:error|failure|failed)\b/gi;

export default function ExecutionInputLogEvent(props) {
  const { index, timestamp, message, timeFormat, selected } = props;
  
  const classes = useStyles();
  
  const markupLinksInMessage = (message) => {
    let markedUpMessage=message;
    const delimiter="|#|";
    const matches=message.match(s3Regex);
    if(matches) {
      let templatedMessage=message;
      const links=[];
      matches.forEach((match, index) => {
        links.push(<a key={"link"+index} href={fmtS3ConsoleLink(match)}>{match}</a>);
        templatedMessage=templatedMessage.replace(match,`${delimiter}${index}${delimiter}`);
      });
      let isLink=false;
      markedUpMessage=
        <React.Fragment>
          {
            templatedMessage.split(delimiter).map((node) => {
              let renderedNode=isLink?links[parseInt(node, 10)]:node;
              isLink=!isLink;
              return renderedNode;
            })
          }
        </React.Fragment>;
    }
    return {
      markedUpMessage, 
      matches,
    };
  };

  let messageClassNames = null;
  
  if(message.startsWith('INPUT: size=')) {
    messageClassNames=clsx(classes.root, classes.controller, classes.controllerInput);
  } else if(message.startsWith('START: memory=')) {
    messageClassNames=clsx(classes.root, classes.controller, classes.controllerStart);
  } else if(message.startsWith('FINISH: result=')) {
    if(message.includes('result=FAILED')) {
      messageClassNames=clsx(classes.root, classes.controller, 
        classes.controllerFinish, classes.controllerFinishFailed);
    } else if(message.includes('result=SUCCEEDED')) {
      messageClassNames=clsx(classes.root, classes.controller, 
        classes.controllerFinish, classes.controllerFinishSuccess);
    } else if(message.includes('result=SKIPPED')) {
      messageClassNames=clsx(classes.root, classes.controller, 
        classes.controllerFinish, classes.controllerFinishSkipped);
    } else {
      messageClassNames=clsx(classes.root, classes.controller, classes.controllerFinish);
    }
  } else if(message.startsWith('POST: PASS')) {
    messageClassNames=clsx(classes.root, classes.controller, 
      classes.controllerFinish, classes.controllerFinishSuccess);
  } else if(message.startsWith('POST: FAIL')) {
    messageClassNames=clsx(classes.root, classes.controller, 
      classes.controllerFinish, classes.controllerFinishFailed);
  } else if(message.startsWith('UPLOAD DATA:') || message.startsWith('UPLOAD ARTIFACTS:')) {
    if(message.includes('FAILED:')) {
      messageClassNames=clsx(classes.root, classes.controller, classes.controllerFinishFailed);
    } else {
      messageClassNames=clsx(classes.root, classes.controller, classes.controllerSample);
    }
  } else {
    if(message.match(errorRegex)) {
      messageClassNames=clsx(classes.root, classes.workload, classes.workloadError);
    } else if(message.includes('WARNING')) {
      messageClassNames=clsx(classes.root, classes.workload, classes.workloadWarning);
    } else if(message.includes('DEBUG')) {
      messageClassNames=clsx(classes.root, classes.workload, classes.workloadDebug);
    } else {
      messageClassNames=clsx(classes.root, classes.workload);
    }
  }

  const { markedUpMessage, matches }=markupLinksInMessage(message);
  
  if(markedUpMessage!==message) {
    logger.info(`LINKS FOUND: line ${index}: `, matches, markedUpMessage);
  }
  
  return(
    <div className={classes.line} style={selected?{ border: 'thin dotted black'}:{}}>
      <Typography className={clsx(classes.root, classes.timestamp)} noWrap>{fmtTimestamp(timestamp,timeFormat)}</Typography>
      <Typography className={messageClassNames}>{(markedUpMessage && markedUpMessage!==message)?markedUpMessage:message}</Typography>
    </div>
  );
  
}

ExecutionInputLogEvent.propTypes = {
  index: PropTypes.number.isRequired,
  timestamp: PropTypes.number.isRequired,
  message: PropTypes.string.isRequired,
  timeFormat: PropTypes.string,
  selected: PropTypes.bool
};

ExecutionInputLogEvent.defaultProps = {
  timeFormat: 'datetime',
  selected: false,
};
